import * as React from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import CircleIcon from '@mui/icons-material/Circle';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import DeleteIcon from '@mui/icons-material/Delete';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import { Box, TextField, Tooltip } from '@mui/material';
import CellType from '../../../constants/type';
import { dataFormat, isCheckQuant, currencySignPrice1, currencySignPrice2, appCompno } from '../../../config';
import convertImageUrl from '../../../helpers/convertImageUrl';
import getEffectiveOrderQty from '../../../helpers/getEffectiveOrderQty';

const handleInput = (event) => {
    const input = event?.target;
    input.value = input.value.replace(/[^0-9]/g, '');
};

export function setCell(params) {
    const { type, data, action, row, l, handleClickOpenImage, language, index, redBorder } = params;
    const roundSum = 100;

    switch (type) {
        case CellType.CHECKBOX:
            return (
                <Checkbox
                    checked={Boolean(data)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => action ? action(event, CellType.CHECKBOX, row) : () => { console.warn('No action for this checkbox') }}
                />
            );
        case CellType.STRING:
            return l(data);
        case CellType.STRING_WITH_CURRENCY:
            return data + (appCompno === '1' ? currencySignPrice2 : '');
        case CellType.DATE:
            return moment(data).format(dataFormat);
        case CellType.DATEONLY:
            return moment(data).format('DD-MM-YYYY');
        case CellType.BOOLEAN:
            if (data)
                return <CheckCircleIcon color='success' />
            else
                return <CheckCircleIcon color='disabled' />;
        case CellType.BOOLCONNECTED:
            if (data)
                return <WifiIcon color='success' />
            else
                return <WifiOffIcon color='disabled' />;
        case CellType.BOOLSTATUS:
            if (!data) return <CircleIcon color='error' />
            return <CircleIcon color={data} />
        case CellType.BOOLALARM:
            switch (data) {
                case 0:
                    return <ErrorIcon color='error' />
                case 1:
                    return <ErrorIcon color='warning' />
                case 2:
                    return <ErrorIcon color='success' />
                default:
                    return data;
            }
        case CellType.EDITABLE_NUMBER:
            if (row?._ghost) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#1565c0', fontWeight: 600, minWidth: '60px' }}>
                        {data}
                    </Box>
                );
            }
            const effectiveQty = getEffectiveOrderQty(row);
            const isInvalidQty = effectiveQty > 1 && parseInt(data || 0, 10) !== 0 && parseInt(data || 0, 10) % effectiveQty !== 0;
            const invalidQtyTitle = isInvalidQty ? `${l('Quantity must be a multiple of')} ${effectiveQty}` : '';
            return <>
                <Tooltip
                    title={invalidQtyTitle}
                    arrow
                >
                    <TextField
                        id={`number_${index}`}
                        onInput={handleInput}
                        onFocus={(event) => event.target.select()}
                        onChange={(event) => action ? action(event, CellType.EDITABLE_NUMBER, row)
                            : () => { console.warn('No action for this button') }}
                        sx={{
                            maxWidth: { xs: '64px', xl: '80px' },
                            minWidth: { xs: '64px', xl: '80px' },
                            '& .MuiInputBase-input': {
                                color: isInvalidQty ? 'red' : '#212121',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isInvalidQty ? 'red' : undefined,
                            },
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                let indexNumber = index;
                                let nextNumber = document.getElementById(`number_${index + 1}`);

                                if (nextNumber) {
                                    let disabled = nextNumber?.disabled;

                                    while (disabled) {
                                        ++indexNumber;
                                        nextNumber = document.getElementById(`number_${indexNumber}`);
                                        disabled = nextNumber.disabled;
                                    }

                                    nextNumber?.focus();
                                }
                                else {
                                    const firstNumber = document.getElementById(`number_0`);
                                    if (firstNumber) firstNumber?.focus();
                                }
                            }
                        }}
                        defaultValue={data.toString() === '0' || data.toString() === '' ? '0' : data.toString().replace(/^0+/, '')}
                        disabled={isCheckQuant === '1' ? !row?.FMINQTY : false || row?.FINSTOCK === "Not Available"}
                        size='small'
                        type='number'
                        inputProps={{ step: effectiveQty, min: 0 }}
                    />
                </Tooltip>
            </>;
        case CellType.ACTIONS:
            if (row?._ghost) {
                return (
                    <Box display="flex" alignItems="center">
                        <Tooltip title={l('Add to basket')}>
                            <IconButton
                                aria-label="add"
                                sx={{ color: '#1565c0' }}
                                onClick={(event) => action ? action(event, CellType.ACTIONS, row) : () => {}}
                            >
                                <AddShoppingCartIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={l('Dismiss')}>
                            <IconButton
                                aria-label="dismiss"
                                size="small"
                                sx={{ color: '#9e9e9e' }}
                                onClick={(event) => action ? action(event, 'DISMISS_GHOST', row) : () => {}}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            }
            return <IconButton
                aria-label="delete"
                onClick={
                    (event) => action ? action(event, CellType.ACTIONS, row)
                        : () => { console.warn('No action for this button') }}
            >
                <DeleteIcon />
            </IconButton>

        case CellType.IMG:
            const imgUrl = convertImageUrl(data);
            return <img
                src={imgUrl}
                onClick={() => handleClickOpenImage ? handleClickOpenImage(imgUrl)
                    : () => { console.warn('No action for this button') }}
                alt=''
                style={{ maxHeight: '50px', maxWidth: "50px", cursor: 'pointer' }} />

        case CellType.PRODUCT_NAME:
            return <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box>
                    {language === 'ru' ? row.FNAMERUS : row.FNAME || row?.SPEC}
                </Box>
                <Typography
                    variant='p'
                    onClick={
                        (event) => action ? action(event, CellType.PRODUCT_NAME, row)
                            : () => { console.warn('No action for this button') }}
                    className='hover-d detail'
                    sx={{ fontSize: '12px', alignSelf: 'flex-start', pl: 0 }}
                >
                    {l('Details')}
                </Typography>
            </Box>

        case CellType.PRODUCT_SUM:
            return <Box>
                <Box>
                    {data}&nbsp;{currencySignPrice1}
                </Box>
                <Box color="#dc3545">
                    {row.SUMFWEIGHT}&nbsp;{l('Kg')}
                </Box>
                <Box color="#007bff">
                    {row.SUMFVOLUME}&nbsp;{l('m³')}
                </Box>

            </Box>

        case CellType.PRISE_WITH_DISCOUNT:
            return <Box display="flex" alignItems="center" gap="4px">
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="end"
                    minWidth="35px"
                >
                    {row.FDISCOUNT !== 0 && row.FDISCOUNT ? (
                        <>
                            <Box color="gray" sx={{ textDecoration: 'line-through' }}>
                                {Math.round(data * roundSum) / roundSum}{row.compno === 1 ? currencySignPrice1 : ''}
                            </Box>
                            <Box>
                                {Math.round((data / 100 * (100 - row.FDISCOUNT)) * roundSum) / roundSum}{row.compno === 1 ? currencySignPrice1 : ''}
                            </Box>
                            {row.compno === 1 && row.FRRATE ?
                                <Tooltip title={l('Exchange rate') + `: ${row.FRRATE} ₴`}>
                                    <Box>
                                        ~{Math.round((data / 100 * (100 - row.FDISCOUNT)) * row.FRRATE)}₴
                                    </Box>
                                </Tooltip> : ''}

                        </>
                    ) : (
                        <>
                            <Box>
                                {Math.round(data * roundSum) / roundSum}{row.compno === 1 ? currencySignPrice1 : ''}
                            </Box>
                            {row.compno === 1 && row.FRRATE ?
                                <Tooltip title={l('Exchange rate') + `: ${row.FRRATE} ₴`}>
                                    <Box>
                                        ~{Math.round(data * row.FRRATE)}₴
                                    </Box>
                                </Tooltip> : ''}

                        </>
                    )}
                </Box>
                {row.FDISCOUNT !== 0 && row.FDISCOUNT ? (
                    <Tooltip title={l('Discount')}>
                        <Box className="discount-label">
                            -{row.FDISCOUNT}%
                        </Box>
                    </Tooltip>
                ) : ''
                }
            </Box>
        case CellType.FINSTOCK:
            return <Box sx={{ color: data === 'Немає в наявності' || row?.FINSTOCK === "Not Available" ? 'red' : '#212121' }}>
                {l(data)}
            </Box>

        case CellType.ORDERSUM:
            return <Box display="flex" alignItems="center" gap="10px">
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="end"
                    width="35px"
                >
                    <Box>
                        {data + currencySignPrice1}
                    </Box>

                    {row.compno === 1 ?
                        <Tooltip title={l('Exchange rate') + `: ${row.OPTUAHRATE} ₴`}>
                            <Box>
                                ~{Math.round(data * row.OPTUAHRATE)}₴
                            </Box>
                        </Tooltip> : ''}
                </Box>
            </Box>

        case CellType.IS_INSTOCK_MORE_THAN_NUM:
            return (
                row.inStock < 10 && redBorder ?
                    <Box sx={{ color: 'red' }}>{`${l('В наявності')}: ${row.inStock}`}</Box>
                    :
                    <Box>{data > 10 ? `${l('В наявності')} >10` : `${l('В наявності')} <10`}</Box>
            );

        case CellType.PACK_QTY:
            return <Box>{data || 0}</Box>;

        case CellType.MIN_QTY:
            return <Box>{getEffectiveOrderQty(row)}</Box>;

        default:
            return data;
    }
}

export default setCell;
